import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { downloadIcon, plusIcon, screenshotIcon } from '../helpers/icons';
import { Alert, Form, Button, Container, Modal } from 'react-bootstrap';
import SessionDetail from './sessiondetail';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './dashboard.css';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { alertActions, adminActions } from '../../redux/_actions';
import TableComponent from '../helpers/tablecomponent';
import { dateTime } from '../../utilities/conversions';
import {
  generateSessionYoutubeCreation,
  generateOBSTemplate,
  generateSessionCardData,
} from '../../utilities/generate-session-url';
import { accessType, sessionStatus } from '../../redux/_constants';
import { Loading, ScrollLoading } from '../helpers/loading';
import { SearchInput, addBold, noResults } from '../helpers/search';
import StreamCreate from './streamcreate';

function Dashboard() {
  const [isLoading, setIsLoading] = useState(true);
  const [isScrollLoading, setIsScrollLoading] = useState(false);
  const alert = useSelector((state) => state.alert);
  const sessions = useSelector((state) => state.admin.sessions);
  const sessionTeams = useSelector((state) => state.admin.sessionTeams);
  const sessionJudges = useSelector((state) => state.admin.sessionJudges);
  const sessionStreams = useSelector((state) => state.admin.streams);
  // const teams = useSelector((state) => state.admin.teams?.items || []);
  // const nextToken = useSelector((state) => state.admin.sessions.tokens.nextToken);
  const searchQuery = useSelector(
    (state) => state.admin.sessions.tokens.searchQuery
  );
  // const searchToken = useSelector((state) => state.admin.sessions.tokens.searchToken);

  const [searchTerm, setSearchTerm] = useState(searchQuery || '');
  const [tempSearchTerm, setTempSearchTerm] = useState(searchQuery || '');
  const [filters, setFilters] = useState({
    gender: null,
    status: null,
    start: null,
    demo: null,
    startDate: null,
    endDate: null,
  });
  // Server-side date range filter: 'near' (±7 days), '+30', '+90', 'all'
  const [dateRangeFilter, setDateRangeFilter] = useState('near');

  // Helper to compute date range for server query
  const getDateRangeForFilter = (filterValue) => {
    const now = new Date();
    switch (filterValue) {
      case 'near': {
        const start = new Date(now);
        start.setDate(start.getDate() - 7);
        const end = new Date(now);
        end.setDate(end.getDate() + 7);
        return { startDate: start.toISOString(), endDate: end.toISOString() };
      }
      case '+30': {
        const start = new Date(now);
        start.setDate(start.getDate() - 7);
        const end = new Date(now);
        end.setDate(end.getDate() + 30);
        return { startDate: start.toISOString(), endDate: end.toISOString() };
      }
      case '+90': {
        const start = new Date(now);
        start.setDate(start.getDate() - 7);
        const end = new Date(now);
        end.setDate(end.getDate() + 90);
        return { startDate: start.toISOString(), endDate: end.toISOString() };
      }
      case 'all':
      default:
        return null; // No date filter
    }
  };
  const [selectedSession, setSelectedSession] = useState(null);
  const [showStreamCreate, setShowStreamCreate] = useState(null);

  const dispatch = useDispatch();
  const numSessions = sessions?.allIds?.length || 0;
  const isSearch = searchTerm !== '';
  // let lastScrollTop = 0;

  const isFirstRender = useRef(true);

  // Single effect handles both initial load and date range changes
  useEffect(() => {
    if (isFirstRender.current) {
      dispatch(alertActions.clear());
      isFirstRender.current = false;
    }
    setIsLoading(true);
    const dateRange = getDateRangeForFilter(dateRangeFilter);
    dispatch(adminActions.getSessions({ dateRange, nextToken: null }));
  }, [dateRangeFilter, dispatch]);

  // Only stop loading when we actually have sessions data
  useEffect(() => {
    if (sessions?.allIds?.length > 0) {
      setIsLoading(false);
      setIsScrollLoading(false);
    }
    setSearchTerm(tempSearchTerm);
  }, [sessions, tempSearchTerm]);

  const headers = useMemo(
    () => [
      { Header: '#', accessor: 'order' },
      {
        Header: 'Name',
        accessor: 'name',
        maxWidth: 50,
        Cell: (r) => addBold(r.cell.value, searchTerm),
      },
      { Header: 'Type', accessor: 'type' },
      { Header: 'Gender', accessor: 'gender' },
      {
        Header: 'Key',
        accessor: 'key',
        Cell: ({ row }) => (
          <Link
            to={{
              pathname: '/session',
              search: `?s=${row.values.key}`,
              state: { fromAdmin: true },
            }}
            target="_blank"
            rel="noopener noreferrer"
          >
            {row.values.key}
          </Link>
        ),
      },
      {
        Header: 'RTN',
        accessor: 'rtnId',
        Cell: (r) => {
          const { row, column, cell, updateMyData } = r;
          const [isEditing, setIsEditing] = useState(false);
          const [value, setValue] = useState(cell.value);
          const initialValue = cell.value;

          // Update value when cell.value changes
          useEffect(() => {
            setValue(cell.value);
          }, [cell.value]);

          const handleSave = async (newValue) => {
            const payload = {
              ...sessions.byId[row.original.id],
              rtnId: newValue,
            };
            await dispatch(adminActions.editSession(payload));
            updateMyData(row.index, column.id, newValue);
            setValue(newValue); // Update local state
            setIsEditing(false);
          };

          const handleCancel = () => {
            setValue(initialValue);
            setIsEditing(false);
          };

          if (isEditing) {
            return (
              <Form.Control
                size="sm"
                type="text"
                value={value}
                className="rtn-id-input"
                onChange={(e) => setValue(e.target.value)}
                onBlur={() => handleSave(value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') {
                    handleSave(value);
                  } else if (e.key === 'Escape') {
                    handleCancel();
                  }
                }}
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            );
          }
          return (
            <div
              onClick={(e) => {
                if (!isEditing) {
                  e.stopPropagation();
                  setIsEditing(true);
                }
              }}
              style={{ cursor: 'text' }}
            >
              {value || plusIcon}
            </div>
          );
        },
      },
      {
        Header: 'Created',
        accessor: 'created',
        Cell: ({ row }) => dateTime(row.values.created),
      },
      {
        Header: 'Start',
        accessor: 'start',
        Cell: ({ row }) => dateTime(row.values.start),
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: (r) => {
          const { row, column, cell, updateMyData } = r;
          return (
            <Form.Control
              as="select"
              label="Status"
              value={cell.value}
              onChange={async (e) => {
                const payload = {
                  ...sessions.byId[row.original.id],
                  status: e.target.value,
                };
                await dispatch(adminActions.editSession(payload));
                updateMyData(row.index, column.id, e.target.value);
              }}
            >
              {Object.keys(sessionStatus).map((statusType) => (
                <option key={statusType} value={statusType}>
                  {statusType}
                </option>
              ))}
            </Form.Control>
          );
        },
      },
      {
        Header: 'Access',
        accessor: 'access',
        Cell: (r) => {
          const { row, column, cell, updateMyData } = r;
          return (
            <Form.Control
              as="select"
              label="Access"
              value={cell.value}
              onChange={async (e) => {
                const payload = {
                  ...sessions.byId[row.original.id],
                  access: e.target.value,
                };
                await dispatch(adminActions.editSession(payload));
                updateMyData(row.index, column.id, e.target.value);
              }}
            >
              {Object.keys(accessType).map((accessValue) => (
                <option key={accessValue} value={accessValue}>
                  {accessValue}
                </option>
              ))}
            </Form.Control>
          );
        },
      },
      {
        Header: 'Scorekeeper',
        accessor: 'scorekeepers',
        Cell: ({ row }) => {
          const scorekeepers = Array.isArray(row.original.scorekeepers)
            ? row.original.scorekeepers
            : [];
          return (
            <div style={{ whiteSpace: 'pre-wrap' }}>
              {scorekeepers.map((email, index) => {
                return <div key={index}>{email || 'N/A'}</div>;
              })}
            </div>
          );
        },
      },
      {
        Header: 'Streams',
        accessor: 'streams',
        Cell: ({ row }) => {
          const streams = Array.isArray(row.original.streams)
            ? row.original.streams
            : [];
          return (
            <div className="streams-cell d-flex flex-column">
              <div className="d-flex flex-wrap align-items-center gap-1">
                {streams.map((stream, index) => (
                  <div key={index} style={{ marginRight: '0.4rem' }}>
                    {stream || 'N/A'}
                  </div>
                ))}
                <Button
                  variant="light"
                  className="stream-add-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowStreamCreate(row.original.id);
                  }}
                >
                  {plusIcon}
                </Button>
              </div>
            </div>
          );
        },
      },
      {
        Header: 'YT',
        accessor: 'youtube',
        Cell: ({ row }) => {
          const session = sessions.byId[row.original.id];
          return (
            <Button
              variant="outline-secondary"
              className="dl-button"
              size="sm"
              onClick={() => {
                const filename = `${session.name.replace(
                  /\s+/g,
                  '_'
                )}_youtube.json`;
                const content = JSON.stringify(
                  generateSessionYoutubeCreation(session),
                  null,
                  2
                );
                const blob = new Blob([content], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);
              }}
            >
              {downloadIcon}
            </Button>
          );
        },
      },
      {
        Header: 'OBS',
        accessor: 'obs',
        Cell: ({ row }) => {
          const session = sessions.byId[row.original.id];
          return (
            <Button
              variant="outline-secondary"
              className="dl-button"
              size="sm"
              onClick={() => {
                const filename = `${session.name.replace(
                  /\s+/g,
                  '_'
                )}_obs.json`;
                const content = JSON.stringify(
                  generateOBSTemplate(session),
                  null,
                  2
                );
                const blob = new Blob([content], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);
              }}
            >
              {downloadIcon}
            </Button>
          );
        },
      },
      {
        Header: 'Card',
        accessor: 'card',
        Cell: ({ row }) => {
          const session = sessions.byId[row.original.id];
          return (
            <Button
              variant="outline-secondary"
              className="dl-button"
              size="sm"
              onClick={() => {
                const filename = `${session.name.replace(
                  /\s+/g,
                  '_'
                )}_card.json`;
                const cardTeams =
                  session.sessionTeams?.items
                    ?.map((st) => sessionTeams?.byId?.[st]?.team)
                    .filter(Boolean) || [];
                const content = JSON.stringify(
                  generateSessionCardData(session, cardTeams),
                  null,
                  2
                );
                const blob = new Blob([content], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);
              }}
            >
              {downloadIcon}
            </Button>
          );
        },
      },
      {
        Header: 'Snapshot',
        accessor: 'snapshot',
        Cell: ({ row }) => {
          const session = sessions.byId[row.original.id];
          return (
            <>
              <Button
                variant="outline-secondary"
                className="dl-button me-1"
                size="sm"
                onClick={async () => {
                  const req = `https://api.virti.us/session/${session.sessionKey}/snapshot`;
                  try {
                    dispatch(
                      alertActions.notification(
                        `Requesting snapshot for session: ${session.name}`
                      )
                    );
                    const response = await axios.get(req, {
                      headers: { 'Content-Type': 'application/json' },
                    });
                    if (response?.data) {
                      dispatch(
                        alertActions.success(
                          `Snapshot generated successfully for session: ${session.name}`
                        )
                      );
                    }
                  } catch (error) {
                    console.error(error);
                    dispatch(
                      alertActions.error(
                        `Failed to take snapshot for session: ${session.name}`
                      )
                    );
                  }
                }}
              >
                {screenshotIcon}
              </Button>
              <Button
                variant="outline-secondary"
                className="dl-button"
                size="sm"
                onClick={async () => {
                  const req = `https://api.virti.us/session/${session.sessionKey}/snapshot`;
                  try {
                    const response = await axios.get(req, {
                      headers: { 'Content-Type': 'application/json' },
                    });
                    const data = response?.data;
                    if (data) {
                      const timestamp = new Date().toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        timeZoneName: 'short',
                      });

                      // Download XML snapshot
                      const xmlResponse = await axios.get(`${req}/xml`, {
                        responseType: 'blob',
                      });
                      const xmlBlob = new Blob([xmlResponse.data]);
                      const xmlUrl = window.URL.createObjectURL(xmlBlob);
                      const xmlLink = document.createElement('a');
                      xmlLink.href = xmlUrl;
                      xmlLink.download = `snapshot-${
                        session.sessionKey
                      }-${timestamp.replace(/[:.]/g, '-')}.xml`;
                      document.body.appendChild(xmlLink);
                      xmlLink.click();
                      document.body.removeChild(xmlLink);
                      window.URL.revokeObjectURL(xmlUrl);

                      // Download JSON snapshot
                      const jsonResponse = await axios.get(`${req}/json`, {
                        responseType: 'blob',
                      });
                      const jsonBlob = new Blob([jsonResponse.data]);
                      const jsonUrl = window.URL.createObjectURL(jsonBlob);
                      const jsonLink = document.createElement('a');
                      jsonLink.href = jsonUrl;
                      jsonLink.download = `snapshot-${
                        session.sessionKey
                      }-${timestamp.replace(/[:.]/g, '-')}.json`;
                      document.body.appendChild(jsonLink);
                      jsonLink.click();
                      document.body.removeChild(jsonLink);
                      window.URL.revokeObjectURL(jsonUrl);

                      dispatch(
                        alertActions.success(
                          `Snapshots downloaded successfully for session: ${session.name}`
                        )
                      );
                    }
                  } catch (error) {
                    console.error(error);
                    dispatch(
                      alertActions.error(
                        `Failed to download snapshots for session: ${session.name}`
                      )
                    );
                  }
                }}
              >
                {downloadIcon}
              </Button>
            </>
          );
        },
      },
    ],
    [searchTerm, sessions, dispatch]
  );

  const data = useMemo(() => {
    let filteredData = sessions.allIds.map((id, i) => {
      const {
        name,
        type,
        gender,
        sessionKey,
        createdAt,
        startAt,
        access,
        status,
        rtnId,
        sessionJudges: scorekeepers,
        streams,
      } = sessions.byId[id];

      return {
        order: i + 1,
        name: name,
        type: type,
        gender: gender,
        key: sessionKey,
        rtnId: rtnId || '',
        created: createdAt,
        start: startAt,
        status: status,
        access: access,
        id: id,
        scorekeepers:
          scorekeepers?.items?.map(
            (sj) => sessionJudges.byId[sj]?.user?.email
          ) || [],
        streams:
          streams?.items?.map((stream) => {
            const streamId = typeof stream === 'string' ? stream : stream.id;
            return sessionStreams.byId[streamId]?.title;
          }) || [],
      };
    });

    if (filters.gender) {
      filteredData = filteredData.filter(
        (row) => row.gender === filters.gender
      );
    }
    if (filters.status) {
      filteredData = filteredData.filter(
        (row) => row.status === filters.status
      );
    }
    if (filters.start === 'upcoming') {
      const today = new Date();
      const nextWeek = new Date(today);
      nextWeek.setDate(today.getDate() + 7);
      filteredData = filteredData.filter((row) => {
        const startDate = new Date(row.start);
        return startDate >= today && startDate <= nextWeek;
      });
    } else if (filters.start === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      filteredData = filteredData.filter((row) => {
        const startDate = new Date(row.start);
        return startDate >= today && startDate < tomorrow;
      });
    }
    if (filters.demo === 'hide') {
      filteredData = filteredData.filter((row) => {
        return !row.name.toLowerCase().includes('demo');
      });
    }
    if (filters.startDate) {
      filteredData = filteredData.filter((row) => {
        const sessionDate = new Date(row.start);
        const startDate = new Date(filters.startDate);
        startDate.setHours(0, 0, 0, 0);

        if (filters.endDate) {
          const endDate = new Date(filters.endDate);
          endDate.setHours(23, 59, 59, 999);
          return sessionDate >= startDate && sessionDate <= endDate;
        }

        return sessionDate.toDateString() === startDate.toDateString();
      });
    }

    return filteredData;
  }, [sessions, sessionJudges, sessionStreams, filters]);

  const FilterPill = ({ label, value, filterKey, className = '' }) => (
    <Button
      variant={filters[filterKey] === value ? 'secondary' : 'outline-secondary'}
      className={`rounded-pill py-0 px-2 mx-1 ${className}`}
      size="sm"
      onClick={() =>
        setFilters((prev) => ({
          ...prev,
          [filterKey]: prev[filterKey] === value ? null : value,
        }))
      }
    >
      {label}
    </Button>
  );

  const handleSearch = (query) => {
    setTempSearchTerm(query);
    if (query && query !== '') {
      const dateRange = getDateRangeForFilter(dateRangeFilter);
      dispatch(
        adminActions.getSessions({
          searchQuery: query,
          dateRange,
          nextToken: null,
        })
      );
    } else {
      setSearchTerm(query);
      // Re-fetch with current date range when clearing search
      const dateRange = getDateRangeForFilter(dateRangeFilter);
      dispatch(adminActions.getSessions({ dateRange, nextToken: null }));
    }
  };

  return (
    <Container fluid className="dashboard-container">
      <div className="dashboard-header">
        <div className="d-flex align-items-center mb-3" style={{ gap: '1rem' }}>
          <Link
            to="/admin"
            className="d-flex align-items-center justify-content-center"
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              backgroundColor: '#e9ecef',
              textDecoration: 'none',
              flexShrink: 0,
            }}
            title="Admin Home"
          >
            <svg width="20" height="20" viewBox="0 0 512 512">
              <path
                d="M217.668 439.078h72.7L424 72.486h-74.748L270.4 293.158l-14.336 47.1h-4.1l-13.312-47.1-75.768-220.672H88.132z"
                fill="#0d6efd"
              />
            </svg>
          </Link>
          <div
            className="d-flex"
            role="group"
            style={{
              borderRadius: '20px',
              overflow: 'hidden',
              border: '2px solid #0d6efd',
              height: '36px',
              width: 'fit-content',
            }}
          >
            <span
              className="d-flex align-items-center"
              style={{
                backgroundColor: '#0d6efd',
                color: 'white',
                padding: '0 1rem',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              Sessions
            </span>
            <Link
              to="/admin/live"
              className="d-flex align-items-center"
              style={{
                backgroundColor: 'white',
                color: '#0d6efd',
                padding: '0 1rem',
                fontWeight: 600,
                fontSize: '0.875rem',
                textDecoration: 'none',
              }}
            >
              Live
            </Link>
          </div>
          <SearchInput searchTerm={searchTerm} search={handleSearch} />
        </div>
        <div className="filter-container">
          {/* Server-side date range filters */}
          <Button
            variant={
              dateRangeFilter === 'near' ? 'secondary' : 'outline-secondary'
            }
            className="rounded-pill py-0 px-2 mx-1"
            size="sm"
            onClick={() => setDateRangeFilter('near')}
          >
            ±7 Days
          </Button>
          <Button
            variant={
              dateRangeFilter === '+30' ? 'secondary' : 'outline-secondary'
            }
            className="rounded-pill py-0 px-2 mx-1"
            size="sm"
            onClick={() => setDateRangeFilter('+30')}
          >
            +30 Days
          </Button>
          <Button
            variant={
              dateRangeFilter === '+90' ? 'secondary' : 'outline-secondary'
            }
            className="rounded-pill py-0 px-2 mx-1"
            size="sm"
            onClick={() => setDateRangeFilter('+90')}
          >
            +90 Days
          </Button>
          <Button
            variant={
              dateRangeFilter === 'all' ? 'secondary' : 'outline-secondary'
            }
            className="rounded-pill py-0 px-2 mx-1"
            size="sm"
            onClick={() => setDateRangeFilter('all')}
          >
            All
          </Button>
          <span className="mx-2 text-muted">|</span>
          {/* Client-side filters */}
          <FilterPill label="Male" value="MALE" filterKey="gender" />
          <FilterPill label="Female" value="FEMALE" filterKey="gender" />
          <FilterPill label="LIVE" value="LIVE" filterKey="status" />
          <FilterPill label="Hide Demos" value="hide" filterKey="demo" />
          <FilterPill label="Today" value="today" filterKey="start" />
          <FilterPill label="+7 Days" value="upcoming" filterKey="start" />
          <DatePicker
            selectsRange={true}
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={(update) => {
              const [start, end] = update;
              setFilters((prev) => ({
                ...prev,
                startDate: start,
                endDate: end,
              }));
            }}
            dateFormat="MM/dd/yyyy"
            className="date-picker-pill"
            placeholderText="Date Range"
            isClearable
            customInput={
              <Button
                size="sm"
                variant={filters.startDate ? 'secondary' : 'outline-secondary'}
                className="rounded-pill py-0 px-3"
              >
                {filters.startDate
                  ? filters.endDate
                    ? `${new Date(
                        filters.startDate
                      ).toLocaleDateString()} - ${new Date(
                        filters.endDate
                      ).toLocaleDateString()}`
                    : new Date(filters.startDate).toLocaleDateString()
                  : 'Date Range'}
              </Button>
            }
          />
          <Button
            variant="outline-primary"
            size="sm"
            className="rounded-pill py-0 px-3 ms-2"
            onClick={() => {
              const cardData = data.map((row) => {
                const session = sessions.byId[row.id];
                const cardTeams =
                  session.sessionTeams?.items
                    ?.map((st) => sessionTeams.byId?.[st]?.team)
                    .filter(Boolean) || [];
                return generateSessionCardData(session, cardTeams);
              });
              navigator.clipboard.writeText(JSON.stringify(cardData, null, 2));
            }}
          >
            Copy All Cards
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            className="rounded-pill py-0 px-3 ms-2"
            onClick={() => {
              const ytData = data.map((row) => {
                const session = sessions.byId[row.id];
                return generateSessionYoutubeCreation(session);
              });
              const blob = new Blob([JSON.stringify(ytData, null, 2)], {
                type: 'application/json',
              });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', 'youtube_streams.json');
              document.body.appendChild(link);
              link.click();
              link.parentNode.removeChild(link);
              window.URL.revokeObjectURL(url);
            }}
          >
            YT Stream Gen {downloadIcon}
          </Button>
        </div>
      </div>

      {!alert.clear && alert.message && (
        <Alert
          dismissible
          onClose={() => dispatch(alertActions.clear())}
          variant={alert.type === 'alert-danger' ? 'danger' : 'success'}
          className="dashboard-alert"
        >
          {alert.message}
        </Alert>
      )}

      {/* Add auto-dismiss effect for alerts */}
      {useEffect(() => {
        if (alert.message) {
          const timer = setTimeout(() => {
            dispatch(alertActions.clear());
          }, 5000); // 5 seconds
          return () => clearTimeout(timer);
        }
      }, [alert.message, dispatch])}

      <div className="dashboard-content">
        {isLoading || numSessions === 0 ? (
          <Loading />
        ) : data.length === 0 && !isSearch ? (
          noResults('sessions')
        ) : (
          <TableComponent
            columns={headers}
            data={data}
            initialState={{
              sortBy: [
                {
                  id: 'start',
                  desc: !(
                    filters.start === 'upcoming' ||
                    filters.start === 'today' ||
                    filters.status === 'LIVE' ||
                    filters.startDate
                  ),
                },
              ],
            }}
            searchTerm={searchTerm}
            textSort={true}
            showFooter={true}
            filterColumns={['name']}
            onClick={(e, row) => {
              // Don't trigger if clicking on a button or select
              if (
                e.target.tagName === 'BUTTON' ||
                e.target.tagName === 'SELECT' ||
                e.target.closest('button') ||
                e.target.closest('select')
              ) {
                return;
              }
              setSelectedSession(row.id);
            }}
          />
        )}
        {isScrollLoading && <ScrollLoading />}
      </div>

      <Modal
        show={selectedSession !== null}
        onHide={() => setSelectedSession(null)}
        size="xl"
        centered
        dialogClassName="session-modal"
        animation={false}
      >
        {selectedSession && (
          <SessionDetail
            key={selectedSession}
            sessionId={selectedSession}
            sessionKey={sessions.byId[selectedSession]?.sessionKey}
            back={() => setSelectedSession(null)}
          />
        )}
      </Modal>

      <StreamCreate
        show={showStreamCreate !== null}
        onHide={() => setShowStreamCreate(null)}
        sessionId={showStreamCreate}
      />
    </Container>
  );
}

export default Dashboard;
